import type { AxiosError } from 'axios';
import axiosInstance from './interceptor.services';

export type TScheduleType = 'regular' | 'special';
export type TWeekday =
  | 'monday'
  | 'tuesday'
  | 'wednesday'
  | 'thursday'
  | 'friday'
  | 'saturday'
  | 'sunday';
export type TScheduleTarget = 'store' | 'click_and_collect';

export interface IStoreSchedulePayload {
  type: TScheduleType;
  weekday?: TWeekday[];
  day?: string[];
  open?: string;
  close?: string;
  isClosed?: boolean;
  slotIntervalMinutes?: number;
  maxOrdersPerSlot?: number;
  orderCutoffDays?: number;
  orderCutoffTime?: string;
}

export interface IStoreSchedule {
  id: string;
  restaurantId: string;
  type: TScheduleType;
  weekday: TWeekday | null;
  day: string | null;
  openTime: string | null;
  closeTime: string | null;
  isClosed: boolean;
  target?: TScheduleTarget;
  slotIntervalMinutes?: number | null;
  maxOrdersPerSlot?: number | null;
  orderCutoffDays?: number | null;
  orderCutoffTime?: string | null;
  createdAt: string;
  updatedAt?: string;
}

export interface IUpdateStoreSchedulePayload {
  weekday: TWeekday;
  day: string;
  open: string;
  close: string;
  slotIntervalMinutes?: number;
  maxOrdersPerSlot?: number;
  orderCutoffDays?: number;
  orderCutoffTime?: string;
}

export interface IOpenAndCloseTime {
  open: string;
  close: string;
  slotIntervalMinutes?: number;
  maxOrdersPerSlot?: number;
  orderCutoffDays?: number;
  orderCutoffTime?: string;
}

export interface IUpdateWeekdaySchedulePayload {
  target?: TScheduleTarget;
  monday?: IOpenAndCloseTime[];
  tuesday?: IOpenAndCloseTime[];
  wednesday?: IOpenAndCloseTime[];
  thursday?: IOpenAndCloseTime[];
  friday?: IOpenAndCloseTime[];
  saturday?: IOpenAndCloseTime[];
  sunday?: IOpenAndCloseTime[];
}

export interface IResolvedSlot {
  start: string;
  end: string;
  available: boolean;
  remainingCapacity?: number | null;
}

export const StoreScheduleService = {
  /**
   * Le "temps de préparation minimum" du mode ASAP correspond au vrai champ
   * serveur `preparationTimeBuffer` du commerce (pas un réglage de schedule) —
   * partagé avec la page Édition du commerce (restaurants/get, restaurants/update).
   * Cette même route renvoie aussi `schedules[]` (tous targets confondus) —
   * source utilisée pour le moment à la place de schedules/search.
   */
  getPreparationTimeBuffer: async (storeId: string) => {
    try {
      const response = await axiosInstance.get<{
        data: {
          preparationTimeBuffer: number;
          schedules?: IStoreSchedule[];
        };
      }>(`restaurants/get/${storeId}`);
      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  updatePreparationTimeBuffer: async (
    storeId: string,
    preparationTimeBuffer: number,
  ) => {
    try {
      const response = await axiosInstance.put(
        `restaurants/update/${storeId}`,
        { preparationTimeBuffer },
      );
      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  addSchedules: async (
    storeId: string,
    schedules: IStoreSchedulePayload[],
    target: TScheduleTarget = 'store',
  ) => {
    try {
      const response = await axiosInstance.post<{
        data: IStoreSchedule[];
        message: string;
      }>(`restaurants/${storeId}/schedules/add`, { schedules, target });
      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  updateSchedule: async (
    storeId: string,
    scheduleId: string,
    payload: IUpdateStoreSchedulePayload,
  ) => {
    try {
      const response = await axiosInstance.put<{
        data: IStoreSchedule;
        message: string;
      }>(`restaurants/${storeId}/schedules/update/${scheduleId}`, payload);
      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  updateWeekdaySchedule: async (
    storeId: string,
    payload: IUpdateWeekdaySchedulePayload,
  ) => {
    try {
      const response = await axiosInstance.put<{ message: string }>(
        `restaurants/${storeId}/schedules/weekday-schedule`,
        payload,
      );
      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  getResolvedSlots: async (
    storeId: string,
    date: string,
    target: TScheduleTarget = 'store',
  ) => {
    try {
      const response = await axiosInstance.get<{
        data: IResolvedSlot[];
        message: string;
      }>(`restaurants/${storeId}/schedules/slots`, {
        params: { date, target },
      });
      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  searchSchedules: async (
    storeId: string,
    target?: TScheduleTarget,
    pagination = false,
  ) => {
    try {
      const response = await axiosInstance.get<{
        data: IStoreSchedule[];
        message: string;
        total: number;
      }>(`restaurants/${storeId}/schedules/search`, {
        params: { pagination, target },
      });
      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  deleteSchedule: async (storeId: string, scheduleId: string) => {
    try {
      const response = await axiosInstance.delete<{ message: string }>(
        `restaurants/${storeId}/schedules/delete/${scheduleId}`,
      );
      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },
};
